import classNames from "classnames";
import {runInAction} from "mobx";
import {observer} from "mobx-react-lite";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {BookingColumnNames} from "../../../shared/enums/eventBookingColumnNames";
import {EventBookingStatus} from "../../../shared/enums/eventBookingStatus";
import {IconSource} from "../../../shared/enums/iconSource";
import {SortOrder} from "../../../shared/enums/sortOrder";
import EventBooking from "../../../stores/domainStore/pages/models/eventBooking";
import {TextObservable} from "../../../stores/observables/textObservable";
import {useStores} from "../../../stores/useStores";
import ButtonIcon from "../../atoms/button/buttonIcon";
import CheckBox, {CheckBoxType} from "../../atoms/checkBox/checkBox";
import Icon from "../../atoms/common/icon/icon";
import HeaderProgressBar from "../../atoms/headerProgressBar/headerProgressBar";
import Tag, {TagType} from "../../atoms/tag/tag";
import AreaTag from "../../atoms/areaTag/areaTag";
import EventBookingSearchDropDown from "../../molecules/eventBookingSearchDropDown/eventBookingSearchDropDown";
import "./eventBookingsTable.sass";
import EventBookingNotes from "../../molecules/eventBookingNotes/eventBookingNotes";
import {EventBookingSource} from "../../../shared/enums/eventBookingSource";
import {EventDetailsHelper} from "../../../shared/eventDetailsHelper";
import ResidentBookingGroup from "../../../stores/domainStore/pages/models/residentBookingGroup";
import Table, {IColumnProps} from "../Table/table";
import TagOptionMenu from "../../atoms/tagOptionMenu/tagOptionMenu";
import {TagKeyValue} from "../../../stores/observables/tagKeyValue";
import CircleIcon from "../../atoms/circleIcon/circleIcon";

export interface IEventBookingsTableProps {
  eventBookings: EventBooking[];
  type?: EventBookingStatus;
  header?: React.ReactNode;
  isSortingEnabled?: boolean;
  showIndex?: boolean;
  isPaidEvent: boolean;
}

const EventBookingsTable: React.FC<IEventBookingsTableProps> = ({
  eventBookings,
  type,
  header,
  isSortingEnabled = true,
  showIndex = true,
  isPaidEvent
}) => {
  const {t} = useTranslation(["translation", "event"]);
  const [searchModeEnabled, setSearchModeEnabled] = useState(false);

  const {
    domainStore: {eventsDomainStore},
    uiStore: {eventsUiStore, modalStore}
  } = useStores();

  useEffect(() => {
    return runInAction(() => {
      eventsUiStore.residentSearchTerm = "";
    });
  }, [eventsUiStore]);

  useEffect(() => eventsUiStore.resetResidents, [eventsUiStore]);

  // Close search results if screen starts to load
  useEffect(() => {
    if (eventsUiStore.isLoadingEventBookings && searchModeEnabled) {
      setSearchModeEnabled(false);
    }
  }, [searchModeEnabled, eventsUiStore.isLoadingEventBookings]);

  // reusable component to use when we need to show a "shimmer"
  const skeleton = (
    <div className="skeleton-wrapper">
      <div className="skeleton" />
    </div>
  );

  const event = eventsDomainStore?.editableEvent;
  const isCancelledOrPostponedEvent =
    EventDetailsHelper.isCancelledOrPostponedEvent(event);
  const isBookingDeadlinePassedWithReleaseDate =
    event.isPassedBookingDeadline && event.releaseDate.date !== null;

  const changeSearchMode = (mode: boolean) => {
    setSearchModeEnabled(mode);
  };

  const sortHandler = (sortField: string, sortOrder: SortOrder): void => {
    const sortColumn = sortField as BookingColumnNames;

    eventsUiStore.sortEventBookings(sortColumn, sortOrder);
  };

  const createBooking = async (bookingGroup: ResidentBookingGroup) => {
    if (type) {
      changeSearchMode(false);

      const addResidentRowIndex =
        eventBookings?.find((b) => b.isAddResident)?.index ??
        eventBookings?.length;
      const isPaid = getIsPaid(type, false);

      await eventsUiStore.addResidentBooking(
        bookingGroup,
        type,
        addResidentRowIndex,
        isPaid
      );
    }
  };

  const onSearchTermChange = () => {
    const searchText = searchTermText.text;
    if (searchText !== "") {
      runInAction(() => {
        eventsUiStore.residentSearchTerm = searchText;
      });
      eventsUiStore.searchResidents();
    } else {
      runInAction(() => {
        eventsUiStore.residentSearchTerm = "";
        eventsUiStore.resetResidents();
      });
    }
  };

  const searchTermText = new TextObservable(
    eventsUiStore.residentSearchTerm || "",
    null,
    undefined,
    onSearchTermChange,
    500
  );

  const headerLabelFormatter = (
    column: IColumnProps<EventBooking>
  ): React.ReactNode => {
    const isColumnSorting =
      eventsUiStore.bookingsSortField === column.dataField;
    const isSortAscending =
      eventsUiStore.bookingsSortOrder === SortOrder.Ascending;
    return (
      <div
        className={classNames("sortable-header", {
          "is-sort": isColumnSorting,
          "index-header-col": column.dataField === BookingColumnNames.Index
        })}
      >
        {column.text}
        {column.sort && (
          <div>
            {isColumnSorting && !isSortAscending && (
              <Icon source={IconSource.toggleUp} />
            )}
            {isColumnSorting && isSortAscending && (
              <Icon source={IconSource.toggleDown} />
            )}
            {!isColumnSorting && <Icon source={IconSource.toggleBoth} />}
          </div>
        )}
      </div>
    );
  };

  const addNewResidentClickHandler = () => {
    changeSearchMode(true);
  };

  const indexFormatter = (eventBooking: EventBooking): React.ReactNode => {
    return (
      <div>
        <div>
          {!eventBooking.isGeneratedRow && (
            <CheckBox
              value={eventsUiStore.selectedBookingIds.has(eventBooking.id)}
              onValueChange={() => {
                // Toggle the selected booking
                runInAction(() =>
                  eventsUiStore.toggleSelectedBooking(eventBooking)
                );
              }}
              disabled={
                isCancelledOrPostponedEvent ||
                isBookingDeadlinePassedWithReleaseDate ||
                eventBooking.isReadOnly
              }
              clickable={!eventsUiStore.isLoadingEventBookings}
              type={CheckBoxType.Table}
              testId={`index-${eventBooking.index}`}
            />
          )}
        </div>
        {showIndex && <div>{eventBooking.index}</div>}
      </div>
    );
  };

  const sourceFormatter = (booking: EventBooking): React.ReactNode => {
    const isNoneStatusBooking =
      booking.source.toLowerCase() === EventBookingSource.None;
    if (isNoneStatusBooking) {
      return;
    }

    // For every row from add resident row
    if (booking.isCapacityRow) {
      return;
    }

    if (booking.isSourceShimmer) {
      return skeleton;
    }

    const isStaffBooking =
      booking.source.toLowerCase() === EventBookingSource.Staff;

    let tagType: TagType = isStaffBooking ? "system" : "digital";
    let text: string = isStaffBooking
      ? t("event:labels.bookingSource.staff")
      : t("event:labels.bookingSource.app");

    const isBusy = booking.isSourceSpinning;
    if (isBusy) {
      tagType = "light";
    }

    // For every row before add resident row
    return <Tag type={tagType} text={text} isBusy={isBusy} />;
  };

  // If last row then returns a "Add Resident" button, otherwise returns formatted resident's name
  const residentNameFormatter = (booking: EventBooking): React.ReactNode => {
    if (booking.isAddResident && type) {
      // In the last row if search is enabled, then show a text box, otherwise show the button.
      if (!searchModeEnabled) {
        return (
          <div className="add-resident-button">
            <ButtonIcon
              type="inline"
              testId="add-resident"
              icon={IconSource.addInvertedRound}
              onClick={addNewResidentClickHandler}
              isDisabled={
                isCancelledOrPostponedEvent ||
                isBookingDeadlinePassedWithReleaseDate
              }
              isClickable={
                !eventsUiStore.isLoadingEventBookings &&
                !eventsUiStore.hasUnsavedBookings
              }
            >
              {t("event:labels.booking.addResident")}
            </ButtonIcon>
          </div>
        );
      }
      return (
        <EventBookingSearchDropDown
          searchTerm={searchTermText}
          rowClickHandler={(bookingGroup) => {
            runInAction(() => {
              eventsUiStore.resetResidents();
            });
            createBooking(bookingGroup);
          }}
          placeholder={t("residentadmin:labels.searchPlaceholder")}
          onBlur={() => changeSearchMode(false)}
          type={type}
        />
      );
    }

    if (booking.isReservedRow) {
      return (
        <div className="reserved-slot">
          <ButtonIcon type="inline" icon={IconSource.block} isReadOnly={true}>
            {t("event:labels.booking.capacityReserved")}
          </ButtonIcon>
        </div>
      );
    }

    if (booking.isCapacityRow) {
      return;
    }

    const bookingGroup = eventsUiStore.getBookingGroup(booking.creatorId);
    const isPlusOne = bookingGroup?.length > 1;
    const isChildBooking =
      booking.creatorId !== booking.personId &&
      booking.source === EventBookingSource.App;

    const showGuestIcon = isChildBooking || isPlusOne;

    if (booking.isLoadingResidentDetails) {
      return skeleton;
    }

    return (
      <div className="resident-details">
        <div className="name">
          <div>
            {isChildBooking && <span>+</span>}
            {booking.residentData.name}
          </div>

          {showGuestIcon && <CircleIcon icon={IconSource.guest} />}
        </div>
        <div>{booking.residentData.unitCode}</div>
        <div>{booking.residentData.phoneNumber}</div>
      </div>
    );
  };

  const onNotesUpdated = (eventBooking: EventBooking, staffNote?: string) => {
    runInAction(() => {
      eventBooking.notes.staffNote = staffNote;
    });
    eventsUiStore.updateEventBooking(eventBooking, true);
  };

  // This method checks if the booking is child booking as part of +1 booking
  //   group or a booking on behalf. If yes, then returns the creator name.
  const getPlusOneCreatorNameForChildBooking = (
    eventBooking: EventBooking
  ): string | undefined => {
    const isBookingOnBehalf =
      eventBooking.creatorId !== eventBooking.personId &&
      eventBooking.source === EventBookingSource.App;

    if (isBookingOnBehalf) {
      return eventBooking.creatorName;
    }
  };

  const notesFormatter = (eventBooking: EventBooking): React.ReactNode => {
    // We cannot show the notes until we have a valid id from the server, or it's not a booking row.
    if (eventBooking.isGeneratedRow) {
      return;
    }

    return (
      <EventBookingNotes
        staffNote={eventBooking.notes.staffNote}
        residentNote={eventBooking.notes.residentNote}
        onStaffNoteUpdated={(staffNote) => {
          onNotesUpdated(eventBooking, staffNote);
        }}
        preventEdit={
          eventsUiStore.isLoadingEventBookings ||
          eventsUiStore.hasUnsavedBookings
        }
        bookingOnBehalfCreatorName={getPlusOneCreatorNameForChildBooking(
          eventBooking
        )}
      />
    );
  };

  const areaFormatter = (eventBooking: EventBooking): React.ReactNode => {
    if (eventBooking.isGeneratedRow) {
      return;
    }

    if (eventBooking.isLoadingResidentDetails) {
      return skeleton;
    }

    return <AreaTag personTypeId={eventBooking.residentData.personTypeId} />;
  };

  const statusFormatter = (eventBooking: EventBooking): React.ReactNode => {
    const isBookingToBeRefunded =
      isCancelledOrPostponedEvent && eventBooking.isPaid;
    // For every row after add resident row
    if (eventBooking.isGeneratedRow) {
      return;
    }

    const showTag =
      isCancelledOrPostponedEvent ||
      event?.isPassedEndDate ||
      isBookingDeadlinePassedWithReleaseDate ||
      eventBooking.isStatusSpinning ||
      eventBooking.isStatusShimmer;

    if (showTag) {
      return statusTag(eventBooking, isBookingToBeRefunded);
    }

    return statusDropdown(eventBooking);
  };

  const statusTag = (
    eventBooking: EventBooking,
    isBookingToBeRefunded: boolean | undefined
  ) => {
    let type: TagType;
    let text: string;
    let children: React.ReactNode | undefined = undefined;

    switch (eventBooking.status) {
      case EventBookingStatus.Confirmed:
        type = "success";
        text = t("event:labels.bookingStatus.confirmed");
        break;
      case EventBookingStatus.Waitlist:
        type = "warning";
        text = t("event:labels.bookingStatus.waitlist");
        break;
      case EventBookingStatus.Cancelled:
        type = "negative";
        text = t("event:labels.bookingStatus.cancelled");
        break;
      default:
        type = "neutral";
        text = t("event:labels.bookingStatus.unknown");
        break;
    }

    if (eventBooking.isStatusShimmer) {
      return skeleton;
    }

    const isBusy = eventBooking.isStatusSpinning;

    if (
      event?.isPassedEndDate ||
      isBookingDeadlinePassedWithReleaseDate ||
      isBusy
    ) {
      type = "light";
    }

    // Override if the event is cancel/postponed, this takes precedence over readonly (past)
    if (isBookingToBeRefunded) {
      type = "negative";
      children = (
        <>
          <Icon source={IconSource.error} /> {text}
        </>
      );
    }

    return <Tag type={type} text={text} children={children} isBusy={isBusy} />;
  };

  const confirmOptionKey = "1";
  const waitlistOptionKey = "2";
  const cancelOptionKey = "3";

  const hasRemainingCapacity =
    event?.bookingSpacesDetails?.isUnlimitedCapacity ||
    (event?.bookingSpacesDetails?.capacityRemaining || 0) > 0;

  const statusDropdown = (eventBooking: EventBooking) => {
    const dropdownOptions: TagKeyValue[] = [];

    if (eventBooking.status !== EventBookingStatus.Confirmed) {
      dropdownOptions.push(
        generateStatusDropdownItem(
          EventBookingStatus.Confirmed,
          !hasRemainingCapacity
        )
      );
    }

    if (eventBooking.status !== EventBookingStatus.Waitlist) {
      dropdownOptions.push(
        generateStatusDropdownItem(
          EventBookingStatus.Waitlist,
          eventBooking.status === EventBookingStatus.Confirmed
        )
      );
    }

    if (eventBooking.status !== EventBookingStatus.Cancelled) {
      dropdownOptions.push(
        generateStatusDropdownItem(
          EventBookingStatus.Cancelled,
          event?.isPassedEndDate || isBookingDeadlinePassedWithReleaseDate
        )
      );
    }

    return (
      <TagOptionMenu
        options={dropdownOptions}
        placeHolder={generateStatusDropdownItem(eventBooking.status, false)}
        onOptionSelected={(item: TagKeyValue) => {
          onOptionClicked(item.value, eventBooking);
        }}
        clickable={
          !eventsUiStore.isLoadingEventBookings &&
          !eventsUiStore.hasUnsavedBookings
        }
      />
    );
  };

  const generateStatusDropdownItem = (
    status: EventBookingStatus,
    disabled: boolean
  ): TagKeyValue => {
    switch (status) {
      case EventBookingStatus.Confirmed:
        return new TagKeyValue(
          confirmOptionKey,
          t("event:labels.bookingStatus.confirmed"),
          "success",
          disabled
        );
      case EventBookingStatus.Waitlist:
        return new TagKeyValue(
          waitlistOptionKey,
          t("event:labels.bookingStatus.waitlist"),
          "warning",
          disabled
        );
      case EventBookingStatus.Cancelled:
        return new TagKeyValue(
          cancelOptionKey,
          t("event:labels.bookingStatus.cancelled"),
          "negative",
          disabled
        );
    }
  };

  const handleCancelBooking = (eventBooking: EventBooking) => {
    const contentKey = eventBooking.isPaid
      ? "event:messages.confirmDeleteMultiplePaidBookings"
      : "event:messages.confirmDeleteMultipleBookings";

    modalStore.openModalWithContent({
      content: <>{t(contentKey)}</>,
      title: t("event:titles.deleteBookings"),
      primaryButtonText: t("event:actions.confirmCancel"),
      primaryButtonTestId: "delete-booking-button",
      secondaryButtonText: t("translation:confirmations.negative"),
      centered: true,
      dangerButton: true,
      primaryButtonIcon: IconSource.exit,
      onPrimaryClick: () => {
        runInAction(() => {
          eventBooking.status = EventBookingStatus.Cancelled;
        });
        eventsUiStore.updateEventBooking(eventBooking, true);
        modalStore.toggle();
      },
      onSecondaryClick: modalStore.toggle
    });
  };

  const onOptionClicked = (key: string, eventBooking: EventBooking) => {
    let status: EventBookingStatus;
    switch (key) {
      case cancelOptionKey:
        return handleCancelBooking(eventBooking);
      case waitlistOptionKey:
        status = EventBookingStatus.Waitlist;
        break;
      case confirmOptionKey:
        status = EventBookingStatus.Confirmed;
        break;
    }

    runInAction(() => {
      eventBooking.status = status;
    });

    eventsUiStore.updateEventBooking(eventBooking, true);
  };

  // Only events with paidType.Fixed can set true/false for is paid
  const getIsPaid = (
    eventBookingStatus: EventBookingStatus,
    isChecked: boolean
  ): boolean | undefined => {
    return isPaidEvent ? isChecked : undefined;
  };

  const onPaidToggled = (isChecked: boolean, eventBooking: EventBooking) => {
    runInAction(() => {
      eventBooking.isPaid = getIsPaid(eventBooking.status, isChecked);
    });
    eventsUiStore.updateEventBooking(eventBooking);
  };

  const paidFormatter = (eventBooking: EventBooking): React.ReactNode => {
    // For every row after add resident row
    if (eventBooking.isGeneratedRow) {
      return;
    }

    if (eventBooking.isPaidByFee) {
      return (
        <div>
          <span>{t("event:labels.booking.account")}</span>
        </div>
      );
    }

    return (
      <CheckBox
        value={eventBooking.isPaid!}
        label={t("event:labels.booking.paid")}
        onValueChange={(value: boolean) => onPaidToggled(value, eventBooking)}
        enableBackground={true}
        disabled={eventBooking.status === EventBookingStatus.Waitlist}
        clickable={!eventsUiStore.isLoadingEventBookings}
        type={CheckBoxType.Table}
        testId={`paid-${eventBooking.index}`}
      />
    );
  };

  const rowClassFormatter = (eventBooking: EventBooking) => {
    return classNames({
      "add-resident-row": eventBooking.isAddResident && !searchModeEnabled,
      "capacity-row": eventBooking.isCapacityRow,
      "reserved-row": eventBooking.isReservedRow,
      "disabled-row": eventsUiStore.isLoadingEventBookings
    });
  };

  const columns: IColumnProps<EventBooking>[] = [
    {
      dataField: BookingColumnNames.Index,
      text: t("event:labels.bookingTable.index"),
      classes: "index-col",
      sort: isSortingEnabled,
      formatter: indexFormatter,
      headerFormatter: headerLabelFormatter
    },
    {
      dataField: BookingColumnNames.Source,
      text: t("event:labels.bookingTable.source"),
      classes: "source-col",
      sort: isSortingEnabled,
      formatter: sourceFormatter,
      headerFormatter: headerLabelFormatter
    },
    {
      dataField: BookingColumnNames.Resident,
      text: t("event:labels.bookingTable.resident"),
      classes: "resident-name-col",
      sort: isSortingEnabled,
      formatter: residentNameFormatter,
      headerFormatter: headerLabelFormatter
    },
    {
      dataField: BookingColumnNames.Notes,
      text: t("event:labels.bookingTable.notes"),
      classes: "notes-col",
      sort: isSortingEnabled,
      formatter: notesFormatter,
      headerFormatter: headerLabelFormatter
    },
    {
      dataField: BookingColumnNames.Area,
      text: t("event:labels.bookingTable.area"),
      classes: "area-col",
      sort: false,
      formatter: areaFormatter,
      headerFormatter: headerLabelFormatter
    },
    {
      dataField: BookingColumnNames.Status,
      text: t("event:labels.bookingTable.status"),
      classes: "status-col",
      sort: false,
      formatter: statusFormatter,
      headerFormatter: headerLabelFormatter
    },
    //Adding this item only if the shouldShowIsPaid condition is true
    ...(isPaidEvent
      ? [
          {
            dataField: BookingColumnNames.Paid,
            text: t("event:labels.bookingTable.paid"),
            classes: "paid-col",
            sort: isSortingEnabled,
            formatter: paidFormatter,
            headerFormatter: headerLabelFormatter
          }
        ]
      : [])
  ];

  return (
    <div className="event-bookings-table">
      {header && header}
      <HeaderProgressBar progressBarLoading={eventsUiStore.isLoading} />

      <Table
        data={eventBookings}
        columns={columns}
        keyFormatter={(booking) => booking.id}
        onSort={sortHandler}
        rowClassFormatter={rowClassFormatter}
        sortOrder={eventsUiStore.bookingsSortOrder}
        disabled={eventsUiStore.isLoadingEventBookings}
      />
    </div>
  );
};

export default observer(EventBookingsTable);
